import axios from 'axios'

interface WithRetryOptions {
  /** Number of retries after the first attempt (total attempts = retries + 1). */
  retries?: number
  /** Base backoff in ms; delay for attempt N is `baseMs * 2 ** N`. */
  baseMs?: number
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * A transport failure worth retrying: a request that never received an HTTP
 * response (connection refused / reset / timeout — all carry no `response`),
 * or a 5xx server error. Explicitly NOT retryable:
 *  - an intentional cancellation (AbortSignal / axios cancel token) — the
 *    caller asked to stop, so we must not fire a fresh request;
 *  - any 4xx response (401/403/404/429), a definitive answer from the server —
 *    notably a 401 is the normal "not authenticated" signal that drives the
 *    SSO-discovery flow.
 */
const isTransient = (error: unknown): boolean => {
  // Cancellations carry no `response`, so this must precede the no-response
  // check below (a CanceledError is also an AxiosError in axios 1.x).
  if (axios.isCancel(error)) return false
  if (!axios.isAxiosError(error)) return false
  if (!error.response) return true
  return error.response.status >= 500
}

/**
 * Run `fn`, retrying only transient transport failures with exponential
 * backoff. On exhaustion (or a non-transient error) the ORIGINAL error is
 * rethrown unchanged, so callers can still read `error.response`.
 */
export const withRetry = async <T>(
  fn: () => Promise<T>,
  { retries = 2, baseMs = 200 }: WithRetryOptions = {}
): Promise<T> => {
  let lastError: unknown
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      if (attempt >= retries || !isTransient(error)) throw error
      await sleep(baseMs * 2 ** attempt)
    }
  }
  // Unreachable — the loop always returns or throws — but satisfies the compiler.
  throw lastError
}
